import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { rupiah, fmtDateTime } from "../lib/format";
import { RupiahInput } from "../components/RupiahInput";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash2, Pencil, Wallet, Search, HandCoins } from "lucide-react";
import { toast } from "sonner";

export default function Payroll() {
  const { isViewOnly } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [history, setHistory] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);
  const [payForm, setPayForm] = useState(null);

  const load = () => {
    api.get("/employees").then((r) => setEmployees(r.data));
    api.get("/payroll").then((r) => setHistory(r.data));
  };
  useEffect(() => { load(); }, []);

  const filtered = employees.filter((e) => e.name.toLowerCase().includes(q.toLowerCase()));

  const save = async () => {
    try {
      if (form.id) await api.put(`/employees/${form.id}`, form);
      else await api.post("/employees", form);
      toast.success("Karyawan disimpan"); setForm(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Hapus karyawan ini?")) return;
    try { await api.delete(`/employees/${id}`); toast.success("Karyawan dihapus"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const pay = async () => {
    try {
      await api.post("/payroll/pay", {
        employee_id: payForm.employee_id, amount: payForm.amount,
        deduct_debt: payForm.deduct_debt, note: payForm.note || "",
      });
      toast.success("Gaji dibayarkan & kas tercatat"); setPayForm(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const payEmp = payForm ? employees.find((e) => e.id === payForm.employee_id) : null;
  const deducted = payForm?.deduct_debt && payEmp ? Math.min(payEmp.outstanding_debt, payForm.amount) : 0;
  const netPay = payForm ? payForm.amount - deducted : 0;

  return (
    <div className="space-y-5" data-testid="payroll-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold font-display text-slate-900">Karyawan & Gaji</h1>
          <p className="text-sm text-slate-500">Data karyawan, penggajian, dan potong hutang otomatis</p>
        </div>
        {!isViewOnly && <Button onClick={() => setForm({ name: "", position: "", base_salary: 0 })} className="bg-orange-500 hover:bg-orange-600" data-testid="add-employee-button"><Plus className="h-4 w-4 mr-1.5" /> Tambah Karyawan</Button>}
      </div>

      <Tabs defaultValue="employees">
        <TabsList>
          <TabsTrigger value="employees" data-testid="tab-employees">Karyawan</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-payroll-history">Riwayat Gaji</TabsTrigger>
        </TabsList>

        <TabsContent value="employees" className="space-y-3">
          <div className="relative max-w-sm">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama karyawan..." className="pl-9" data-testid="employee-search-input" />
          </div>
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-slate-50"><TableHead>Nama</TableHead><TableHead>Jabatan</TableHead><TableHead className="text-right">Gaji Pokok</TableHead><TableHead className="text-right">Sisa Hutang</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
              <TableBody>
                {filtered.map((e) => (
                  <TableRow key={e.id} data-testid={`employee-row-${e.id}`}>
                    <TableCell className="font-medium">{e.name}</TableCell>
                    <TableCell className="text-slate-500">{e.position || "-"}</TableCell>
                    <TableCell className="text-right mono">{rupiah(e.base_salary)}</TableCell>
                    <TableCell className="text-right mono font-semibold text-rose-600">{e.outstanding_debt > 0 ? rupiah(e.outstanding_debt) : "-"}</TableCell>
                    <TableCell className="text-right">
                      {!isViewOnly && <>
                        <Button size="sm" variant="outline" onClick={() => setPayForm({ employee_id: e.id, amount: e.base_salary, deduct_debt: e.outstanding_debt > 0, note: "" })} data-testid={`pay-salary-${e.id}`}><HandCoins className="h-3.5 w-3.5 mr-1" /> Bayar Gaji</Button>
                        <Button size="icon" variant="ghost" onClick={() => setForm({ id: e.id, name: e.name, position: e.position, base_salary: e.base_salary })}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" className="text-rose-500" onClick={() => del(e.id)}><Trash2 className="h-4 w-4" /></Button>
                      </>}
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">Tidak ada karyawan</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="history">
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-slate-50"><TableHead>Tanggal</TableHead><TableHead>Karyawan</TableHead><TableHead className="text-right">Gaji Kotor</TableHead><TableHead className="text-right">Potong Hutang</TableHead><TableHead className="text-right">Dibayar</TableHead></TableRow></TableHeader>
              <TableBody>
                {history.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell className="text-xs text-slate-500">{fmtDateTime(h.created_at)}</TableCell>
                    <TableCell className="font-medium">{h.employee_name}</TableCell>
                    <TableCell className="text-right mono">{rupiah(h.gross)}</TableCell>
                    <TableCell className="text-right mono text-rose-600">{h.deducted > 0 ? rupiah(h.deducted) : "-"}</TableCell>
                    <TableCell className="text-right mono font-semibold text-emerald-600">{rupiah(h.net_pay)}</TableCell>
                  </TableRow>
                ))}
                {history.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">Belum ada pembayaran gaji</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>{form?.id ? "Edit" : "Tambah"} Karyawan</DialogTitle></DialogHeader>
          {form && <div className="space-y-3">
            <div className="space-y-1"><Label>Nama</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="employee-name-input" /></div>
            <div className="space-y-1"><Label>Jabatan</Label><Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} data-testid="employee-position-input" /></div>
            <div className="space-y-1"><Label>Gaji Pokok</Label><RupiahInput value={form.base_salary} onChange={(v) => setForm({ ...form, base_salary: v })} data-testid="employee-salary-input" /></div>
          </div>}
          <DialogFooter><Button onClick={save} className="bg-orange-500 hover:bg-orange-600" data-testid="save-employee-button">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!payForm} onOpenChange={(o) => !o && setPayForm(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Wallet className="h-4 w-4" /> Bayar Gaji {payEmp?.name}</DialogTitle></DialogHeader>
          {payForm && <div className="space-y-3">
            <div className="space-y-1"><Label>Nominal Gaji</Label><RupiahInput value={payForm.amount} onChange={(v) => setPayForm({ ...payForm, amount: v })} data-testid="salary-amount-input" /></div>
            {payEmp?.outstanding_debt > 0 && (
              <label className="flex items-center gap-2 text-sm cursor-pointer rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
                <input type="checkbox" checked={payForm.deduct_debt} onChange={(e) => setPayForm({ ...payForm, deduct_debt: e.target.checked })} data-testid="deduct-debt-checkbox" className="h-4 w-4 accent-orange-500" />
                <span>Potong sisa hutang <b className="mono">{rupiah(payEmp.outstanding_debt)}</b></span>
              </label>
            )}
            <div className="rounded-lg bg-slate-100 p-3 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Gaji kotor</span><span className="mono">{rupiah(payForm.amount)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Potong hutang</span><span className="mono text-rose-600">{rupiah(deducted)}</span></div>
              <div className="flex justify-between font-bold border-t border-slate-200 pt-1"><span>Diterima (kas keluar)</span><span className="mono text-emerald-600" data-testid="salary-net-value">{rupiah(netPay)}</span></div>
            </div>
            <div className="space-y-1"><Label>Catatan (opsional)</Label><Input value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} /></div>
          </div>}
          <DialogFooter><Button onClick={pay} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-pay-salary-button">Bayarkan Gaji</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
